import React, { useState } from 'react';
import config from '@config';
import { BOTTOM_INDENTS, COLORS, ROUTE_PATHS, TOP_INDENTS, VALIDATION_RULES } from '@constants';
import { CUSTOM_PLATFORMS, PLATFORM_ID, PLATFORM_NAME } from '@enums';
import { useStore } from '@hooks';
import { Button, Icon, Input, RedirectLink, Select, Typography } from '@ui';
import { Form, Space } from 'antd';
import { observer } from 'mobx-react';
import InviteDeveloper from '../InviteDeveloper';
import CustomForm from './components/Forms/Custom';
import ShopifyForm from './components/Forms/Shopify';
import { PLATFORMS_AUTH } from './constants';
import { StyledButton, StyledCheckbox } from './styled';

const { SHOPIFY, BIGCOMMERCE } = PLATFORM_ID;

const CreateAccount = observer(() => {
    const store = useStore();
    const [form] = Form.useForm();
    const { getEcommerceRedirect, createCustomAccount, hasAccount, isFormPending, setInviteDeveloper } =
        store.authStore;
    const [activeForm, setActiveForm] = useState(SHOPIFY);
    const [showInviteDeveloper, setShowInviteDeveloper] = useState(false);
    const isKlickly = CUSTOM_PLATFORMS.includes(activeForm);
    const isShopify = activeForm === SHOPIFY;
    const isBigcommerce = activeForm === BIGCOMMERCE;
    const addonAfter = '.myshopify.com';

    const onFinishForm = async (values) => {
        if (isShopify) {
            const payload = {
                token_type: 'offline',
                companyName: values.companyName
            };
            if (values.shop) {
                payload.shop = values.shop + addonAfter;
            }
            await getEcommerceRedirect(PLATFORM_NAME.SHOPIFY, payload);
        }

        if (isKlickly) {
            const result = await createCustomAccount(values);

            if (result && !result?.error) {
                setShowInviteDeveloper(true);
                setInviteDeveloper(true);
            }
        }

        if (isBigcommerce) {
            await getEcommerceRedirect(PLATFORM_NAME.BIGCOMMERCE, { companyName: values.companyName });
        }
    };

    const ContinueButton = () => {
        if (isShopify) {
            return (
                <StyledButton
                    backgroundColor={COLORS.$shopify}
                    loading={isFormPending}
                    htmlType='submit'
                    type='primary'
                    block
                    data-e2e='signup_submit_btn'
                >
                    <Space>
                        <div>Continue with Shopify</div>
                        <div>
                            <Icon size='24px' color={COLORS.$white} component={() => <Icon.ShopifyWhite />} />
                        </div>
                    </Space>
                </StyledButton>
            );
        }
        return (
            <Button
                loading={isFormPending}
                htmlType='submit'
                type='primary'
                block
                iconRight={<Icon size='24px' color={COLORS.$white} component={() => <Icon.ArrowForwardIcon />} />}
                data-e2e='signup_submit_btn'
            >
                {hasAccount ? 'Add Store' : 'Continue'}
            </Button>
        );
    };

    if (showInviteDeveloper) {
        return <InviteDeveloper />;
    }

    return (
        <Form data-e2e='signup_setup-store_form' form={form} onFinish={onFinishForm}>
            <Form.Item
                style={BOTTOM_INDENTS.S}
                name='companyName'
                rules={VALIDATION_RULES.REQUIRED}
                onBlur={(e) => form.setFieldValue('companyName', e.target.value.trim())}
            >
                <Input data-e2e='signup_company-name_input' type='text' label='Company Name' />
            </Form.Item>
            <Select
                onChange={(value, option) => {
                    setActiveForm(option.id);
                }}
                defaultValue={SHOPIFY}
                label='Which platform do you currently use?'
                options={PLATFORMS_AUTH}
                data-e2e='signup_select-platform_dropdown'
            />
            {isKlickly && <CustomForm createCustomAccount={createCustomAccount} />}
            {!config.isShopifyStorePageEnabled && isShopify ? (
                <ShopifyForm getEcommerceRedirect={getEcommerceRedirect} />
            ) : null}
            <Form.Item style={TOP_INDENTS.L} name='agreement' valuePropName='checked' rules={VALIDATION_RULES.CHECKBOX}>
                <StyledCheckbox data-e2e='signup_agreement_checkbox' value='agreement'>
                    <Typography.Text data-e2e='signup_agreement_text' color={COLORS.$gray60Black} type='descriptor1'>
                        I have read and agree to the{' '}
                        <RedirectLink
                            data-e2e='signup_terms-of-service_link'
                            text='Terms of Service'
                            style={{ display: 'inline' }}
                            fontSize='16px'
                            to={ROUTE_PATHS.TERMS}
                            target='_blank'
                        />{' '}
                        and{' '}
                        <RedirectLink
                            data-e2e='signup_privacy-policy_link'
                            text='Privacy Policy.'
                            style={{ display: 'inline' }}
                            fontSize='16px'
                            to={ROUTE_PATHS.PRIVACY}
                            target='_blank'
                        />
                    </Typography.Text>
                </StyledCheckbox>
            </Form.Item>
            <Form.Item style={TOP_INDENTS.L}>
                <ContinueButton />
            </Form.Item>
        </Form>
    );
});

export default CreateAccount;
